import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Form } from 'src/shared/components/ui/forms/form'
import { ButtonSelectFormField } from 'src/shared/components/ui/forms/form-fields/button-select'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { professionFormSchema } from 'src/shared/features/users/schemas/user'
import { useEventStore } from 'src/shared/store/event-store'
import { cn } from 'src/shared/utils/cn'

type ProfessionStepProps = {
  onNext: (data: { profession: Profession }) => Promise<void>
  initialData?: Partial<{ profession: Profession }>
  variant?: 'one-column' | 'two-column'
}

const ProfessionStep = ({ onNext, initialData, variant = 'one-column' }: ProfessionStepProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const form = useForm<{ profession: Profession }>({
    resolver: zodResolver(professionFormSchema),
    defaultValues: { profession: initialData?.profession },
  })

  // const [isLoading, setIsLoading] = useState(false)
  const hasInitializedRef = useRef(false)

  // Access only what we need from event store
  const anonymousId = useEventStore((state) => state.anonymousId)
  const sessionId = useEventStore((state) => state.sessionId)
  const flowId = useEventStore((state) => state.flowId)
  const setAnonymousId = useEventStore((state) => state.setAnonymousId)
  const setSessionId = useEventStore((state) => state.setSessionId)
  const setFlowId = useEventStore((state) => state.setFlowId)

  // Setup event IDs at component mount time, only once
  useEffect(() => {
    if (hasInitializedRef.current) return

    if (!anonymousId) {
      setAnonymousId()
    }
    if (!sessionId) {
      setSessionId()
    }
    if (!flowId) {
      setFlowId()
    }

    hasInitializedRef.current = true
  }, [setAnonymousId, setSessionId, setFlowId, anonymousId, sessionId, flowId])

  const onSubmit = async (data: { profession: Profession }) => {
    if (isLoading) return

    setIsLoading(true)
    try {
      // Get the latest values directly from the store
      const currentAnonymousId = useEventStore.getState().anonymousId
      const currentSessionId = useEventStore.getState().sessionId
      const currentFlowId = useEventStore.getState().flowId

      if (currentAnonymousId && currentSessionId && currentFlowId) {
        sendEvent({
          anonymousId: currentAnonymousId,
          eventType: 'flow_step',
          info: {
            ...data,
            flowType: 'onboarding',
            flowStep: 'submit_profession',
            flowId: currentFlowId,
            sessionId: currentSessionId,
          },
        })
      }
      await onNext(data)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2.5 md:space-y-4">
        <ButtonSelectFormField
          control={form.control}
          name="profession"
          options={professionMap}
          showCloseIcon={false}
          gridCols={variant === 'one-column' ? 1 : 2}
          buttonClassName="rounded-[20px] border-none bg-primary text-white text-[16px] font-medium"
          textClassName="text-[16px] font-medium"
          unselectedButtonClassName="bg-accent text-primary"
          fieldClassName="gap-3"
          hideIcon={true}
        />
        <div className="flex w-full justify-center">
          <AuthSubmitButton
            type="submit"
            isLoading={isLoading}
            className={cn('mt-[25px] bg-primary', variant === 'two-column' && 'max-w-[350px]')}
          >
            Finish
          </AuthSubmitButton>
        </div>
        <p className="mt-4 text-center text-[16px] text-muted">
          Conrad currently supports one profession per account, but multi-profession support is coming soon. For now,
          select the one you primarily focus on!
        </p>
      </form>
    </Form>
  )
}

ProfessionStep.displayName = 'ProfessionStep'

export default ProfessionStep
